import { Ship, Anchor } from "lucide-react";
import { CRUISES, CRUISE_NIGHTS_TOTAL } from "@/data/journey";
import useReveal from "@/hooks/useReveal";

/**
 * Chapter III sub-section — Royal Caribbean cruises (Crown & Anchor Society).
 * Reverse-chronological cruise log, each one a card showing the ship, dates,
 * itinerary title, and the chain of ports.
 */
export default function Cruises() {
  const orderedCruises = [...CRUISES].sort((a, b) => b.year - a.year);

  return (
    <section
      id="cruises"
      className="relative z-10 pb-16 sm:pb-24 md:pb-32 bg-[var(--c-bg)] scroll-mt-16 sm:scroll-mt-20"
      data-testid="cruises-section"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 lg:px-16">
        <header className="max-w-3xl mb-10 sm:mb-14">
          <p className="overline mb-3 sm:mb-4">Crown &amp; Anchor Log</p>
          <h2 className="font-serif-display text-3xl xs:text-4xl md:text-5xl text-[var(--c-text)] tracking-tight">
            Ten Royal Caribbean <span className="italic text-[var(--c-accent)]">Cruises</span>
          </h2>
          <div className="hairline my-6 sm:my-8 max-w-xs" />
          <p className="text-[var(--c-text-muted)] text-sm sm:text-base md:text-lg leading-relaxed">
            From Oasis of the Seas in 2010 — when she was the largest cruise ship in the world — to Odyssey of the Seas through the Greek Isles in 2025. Ten sailings across {CRUISE_NIGHTS_TOTAL} nights at sea. Where the exact itinerary for an older sailing couldn't be verified, the ports are marked "to be confirmed".
          </p>
        </header>

        <ol className="space-y-6 sm:space-y-8" data-testid="cruises-list">
          {orderedCruises.map((c) => (
            <CruiseCard key={c.id} cruise={c} />
          ))}
        </ol>
      </div>
    </section>
  );
}

function CruiseCard({ cruise }) {
  const ref = useReveal();
  return (
    <li
      ref={ref}
      className="reveal border border-[var(--c-border)] bg-[var(--c-bg-elev)] p-5 sm:p-6 md:p-8"
      data-testid={`cruise-${cruise.id}`}
    >
      <div className="grid md:grid-cols-[180px,1fr] gap-5 md:gap-10 items-start">
        {/* Year + ship side */}
        <div>
          <p className="font-serif-display italic text-3xl sm:text-4xl text-[var(--c-accent)] leading-none">
            {cruise.year}
          </p>
          <p className="font-mono-plex text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-[var(--c-text-muted)] mt-2">
            {cruise.dates}
          </p>
          <div className="mt-4 flex items-center gap-2 text-[var(--c-text)]">
            <Ship size={14} strokeWidth={1.8} className="text-[var(--c-accent)] shrink-0" />
            <span className="font-serif-display text-sm sm:text-base italic leading-tight">
              {cruise.ship}
            </span>
          </div>
        </div>

        {/* Itinerary side */}
        <div>
          <h3 className="font-serif-display text-lg sm:text-xl md:text-2xl text-[var(--c-text)] leading-snug">
            {cruise.title}
          </h3>
          <p className="font-mono-plex text-[10px] sm:text-xs uppercase tracking-[0.18em] text-[var(--c-text-muted)] mt-1">
            From {cruise.home} · {cruise.points} cruise points
          </p>

          {cruise.note && (
            <p className="mt-3 text-xs sm:text-sm text-[var(--c-text-muted)] italic leading-relaxed max-w-2xl">
              {cruise.note}
            </p>
          )}

          {/* Port chain — or a "ports unknown" notice when we don't have a
              verified itinerary for the specific sailing. */}
          {cruise.portsUnknown ? (
            <div
              className="mt-5 border border-dashed border-[var(--c-border)] px-4 py-3 bg-[var(--c-bg-elev-2)]"
              data-testid={`cruise-${cruise.id}-ports-unknown`}
            >
              <p className="font-mono-plex text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-[var(--c-text-muted)]">
                Ports — to be confirmed
              </p>
              <p className="mt-2 text-xs sm:text-sm text-[var(--c-text-muted)] leading-relaxed max-w-2xl">
                Royal Caribbean's published archive for this specific sailing
                couldn't be verified. Dad — if you remember the ports, let us
                know and we'll add them here.
              </p>
              {cruise.ports.length > 0 && (
                <p className="mt-3 text-xs text-[var(--c-text)] font-serif-display italic">
                  {cruise.ports[0].role === "departed" ? "Departed from " : "From "}
                  <span className="text-[var(--c-accent)]">{cruise.ports[0].name}</span>.
                </p>
              )}
            </div>
          ) : (
            <ol className="mt-5 flex flex-wrap items-center gap-x-2 gap-y-2 text-xs sm:text-sm">
              {cruise.ports.map((p, i) => (
                <li
                  key={p.name + i}
                  className="flex items-center gap-2"
                  data-testid={`cruise-${cruise.id}-port-${i}`}
                >
                  <span className="inline-flex items-center gap-1.5 border border-[var(--c-border)] px-2 py-1 bg-[var(--c-bg-elev-2)]">
                    <Anchor size={10} strokeWidth={1.8} className="text-[var(--c-accent)] shrink-0" />
                    <span className="font-serif-display text-[var(--c-text)]">
                      {p.name}
                    </span>
                  </span>
                  {i < cruise.ports.length - 1 && (
                    <span className="text-[var(--c-accent)] opacity-60 font-mono-plex select-none">
                      →
                    </span>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </li>
  );
}
